import { inject, Injectable } from "@angular/core";

import DOMPurify from "dompurify";

import postcssPrefixSelector from "postcss-prefix-selector";
import postcss, { AcceptedPlugin, RuleProps } from "postcss";
import selectorParser, { ClassName, Identifier, Processor, Tag, Root as AstRoot } from "postcss-selector-parser";

import { RegexUtilityService } from "@main/reporting.v3/core/regex/regex-utility.service";

import { CssRuleSelectorType } from "./models/css-rule-selector-type.enum";
import { CssInjectionScopeDirective } from "./css-injection-scope.directive";
import { MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";


@Injectable()
export class HtmlRenderingService {

    private regexUtilityService: RegexUtilityService = inject(RegexUtilityService);
    private messageService: MessageService = inject(MessageService);

    public sanitizeHtml(html: string): string {
        // Sanitizes HTML using DOMPurify to remove XSS vectors including scripts,
        // javascript: URIs, iframes, objects, embeds, and style tags.
        // onclick is allowed to preserve existing report navigation buttons.
        return DOMPurify.sanitize(html, { FORBID_TAGS: ["style"], ADD_ATTR: ["onclick"] });
    }

    public getCssRules(selector: string): string {
        try {
            if (!selector) return "";

            const css: string = this.getAllCss();

            const root: postcss.Root = postcss.parse(css);

            const collected: string[] = [];

            root.walkRules(rule => {
                // If this rule contains the selector, collect the rule.
                if (!this.matchSelector(rule, selector)) return;

                collected.push(rule.toString());
            });

            const rules: string[] = collected.map(css => this.stripAngularScope(css));

            return rules.join("\n");

        } catch (error) {
            const message = "There is a syntax error in the CSS code. CSS rules not applied"
            this.messageService.add(Message.Error(message));

            return null;
        }
    }

    public getDefaultCssRules(selector: string): string {
        try {
            // Get global and formatted CSS rules that contain the given selector.
            // Removes the selector itself from the rules and the whole CSS rules that points only to this selector.
            // This is used to get styles inside phantom CSS rules (e.g., dashboard-default-styles) whose only purpose
            // is to store the default CSS code that will be shown in the corresponding CSS editors.
            if (!selector) return "";

            const css: string = this.getAllCss();
            const root: postcss.Root = postcss.parse(css);

            const cleaned: postcss.Root = postcss.root();

            // Declare a processor that identifies angular scope attributes and strip them.
            const processor: Processor<void> = this.getAngularScopeStripProcessor();

            root.walkRules(rule => {
                // Only process rules that contain the target selector.
                if (!this.matchSelector(rule, selector)) return;

                const stripped: postcss.Rule = this.stripSelector(rule, selector, processor);

                if (!stripped) return;

                cleaned.append(stripped);
            });

            return cleaned.toString();

        } catch (error) {
            const message = "There is a syntax error in the CSS code. CSS rules not applied"
            this.messageService.add(Message.Error(message));

            return null;
        }
    }

    public getReplacedCssRules(selector: string, replacement: string): string {
        try {
            // Get global CSS rules that contain the given selector.
            // Replaces the occurrences of the selector by the given replacement.
            if (!selector || !replacement) return "";

            const css: string = this.getAllCss();
            const root: postcss.Root = postcss.parse(css);

            const cleaned: postcss.Root = postcss.root();

            // Declare a processor that identifies angular scope attributes and strip them.
            const processor: Processor<void> = this.getAngularScopeStripProcessor();

            root.walkRules(rule => {
                // Only process rules that contain the target selector.
                if (!this.matchSelector(rule, selector)) return;

                const stripped: postcss.Rule = this.replaceSelector(rule, selector, replacement, processor);

                if (!stripped) return;

                cleaned.append(stripped);
            });

            return cleaned.toString();

        } catch (error) {
            const message = "There is a syntax error in the CSS code. CSS rules not applied"
            this.messageService.add(Message.Error(message));

            return null;
        }
    }

    public insertScopedStyles(identifier: string, element: Element, css: string): void {
        try {
            // Inserts the given styles into the given element.
            // Add a scope to the styles based on the given id, to avoid them from bleeding into other components.
            if (!element || !css?.length || !identifier?.length) return;

            const scopeAttribute: string = `[${CssInjectionScopeDirective.ScopeAttribute}="${identifier}"]`;
            const stylesAttribute: string = `[${CssInjectionScopeDirective.StylesAttribute}="${identifier}"]`;

            // Get old style elements with especial attribute pointing to this identifier.
            const styleElements: NodeListOf<Element> = element.querySelectorAll(`style${stylesAttribute}`);

            styleElements.forEach((element: Element): void => element.remove());

            // Create a new <style> element for current styles.
            const styleElement: HTMLStyleElement = document.createElement("style");

            // Set an especial attribute to be able to identify the element to remove it later.
            styleElement.setAttribute(CssInjectionScopeDirective.StylesAttribute, identifier);

            // Add a scope modifier to force the styles to be applied only inside the element with the given id.
            const scopedCss: string = this.getScopedCssCode(css, scopeAttribute);

            // If there was an error parsing the CSS, do not apply any styles at all.
            if (!scopedCss?.length) return;

            // Append the CSS content.
            styleElement.appendChild(document.createTextNode(scopedCss));

            // Attach the <style> to the given element.
            element.appendChild(styleElement);

        } catch (error) {
            const message = "There is a syntax error in the CSS code. CSS rules not applied"
            this.messageService.add(Message.Error(message));

            return null;
        }
    }


    private getAllCss(): string {
        return Array.from(document.styleSheets).flatMap(sheet => {
            try {
                return Array.from(sheet.cssRules).map(rule => rule.cssText);
            } catch {
                return []; // Ignore CORS stylesheets
            }
        }).join("\n");
    }

    private getAngularScopeStripProcessor(): Processor<void> {
        return selectorParser(selectors => {
            selectors.walkAttributes(node => {
                if (node.attribute?.startsWith("_nghost") ||
                    node.attribute?.startsWith("_ngcontent")) {
                    node.remove(); // Remove angular attribute selector entirely
                }
            });
        });
    }

    private getScopedCssCode(css: string, scope: string): string {
        if (!css?.length || !scope?.length) return css;

        const plugins: AcceptedPlugin[] = [
            postcssPrefixSelector({ prefix: scope })
        ];

        const result: postcss.LazyResult = postcss(plugins).process(css);

        return result.css;
    }

    private getCssRuleSelectorType(selector: string): CssRuleSelectorType {
        if (!selector) return null;

        if (selector.startsWith(".")) {
            return CssRuleSelectorType.Class;

        } else if (selector.startsWith("#")) {
            return CssRuleSelectorType.Id;

        } else return CssRuleSelectorType.Tag;
    }


    private stripAngularScope(css: string): string {
        const root: postcss.Root = postcss.parse(css);

        // Declare a processor that identifies angular scope attributes and strip them.
        const processor: Processor<void> = this.getAngularScopeStripProcessor();

        root.walkRules(rule => {
            // Filter rule selectors from angular tags.
            rule.selectors = rule.selectors.map(selector => processor.processSync(selector));
        });

        return root.toString();
    }

    private matchSelector(rule: postcss.Rule, selector: string): boolean {
        if (!selector) return false;

        const type: CssRuleSelectorType = this.getCssRuleSelectorType(selector);

        const value: string = type === CssRuleSelectorType.Tag ? selector : selector.slice(1);

        const parser: Processor = selectorParser();

        // Process each selector in the rule
        for (const current of rule.selectors) {

            // Walk the selector AST for this selector
            const ast: AstRoot = parser.astSync(current);

            let matched: boolean = false;

            switch (type) {
                case CssRuleSelectorType.Class:
                    ast.walkClasses((node: ClassName): boolean => {
                        // If a match is found, return true and stop the visit.
                        if (node.value === value) {
                            matched = true;
                            return false;
                        }
                    });
                    break;

                case CssRuleSelectorType.Id:
                    ast.walkIds((node: Identifier): boolean => {
                        // If a match is found, return true and stop the visit.
                        if (node.value === value) {
                            matched = true;
                            return false;
                        }
                    });
                    break;

                case CssRuleSelectorType.Tag:
                    ast.walkTags((node: Tag): boolean => {
                        // If a match is found, return true and stop the visit.
                        if (node.value.toLowerCase() === value.toLowerCase()) {
                            matched = true;
                            return false;
                        }
                    });
                    break;
            }

            if (matched) return true;
        }

        return false;
    }

    private stripSelector(rule: postcss.Rule, selector: string, processor: Processor<void>): postcss.Rule {
        if (!selector) return null;

        const selectors: string[] = [];

        const type: CssRuleSelectorType = this.getCssRuleSelectorType(selector);

        const value: string = type === CssRuleSelectorType.Tag ? selector : selector.slice(1);

        const parser: Processor = selectorParser();

        // Process each selector in the rule
        for (const current of rule.selectors) {

            // Walk the selector AST for this selector
            const ast: AstRoot = parser.astSync(current);

            // Remove matching target selector nodes from AST
            switch (type) {
                case CssRuleSelectorType.Class:
                    ast.walkClasses(node => {
                        if (node.value === value) node.remove();
                    });
                    break;

                case CssRuleSelectorType.Id:
                    ast.walkIds(node => {
                        if (node.value === value) node.remove();
                    });
                    break;

                case CssRuleSelectorType.Tag:
                    ast.walkTags(node => {
                        if (node.value.toLowerCase() === value.toLowerCase()) node.remove();
                    });
                    break;
            }

            const clean: string = processor.processSync(ast.toString()).trim();

            if (!!clean) selectors.push(clean);
        }

        if (!selectors.length) return null;

        // Create a new rule with the new selectors.
        const props: RuleProps = { selector: selectors.join(", ") };

        // Clone declarations from original rule.
        const children: postcss.NewChild[] = rule.nodes.map(node => node.clone());

        return postcss.rule(props).append(...children);
    }

    private replaceSelector(
        rule: postcss.Rule,
        selector: string,
        replacement: string,
        processor: Processor<void>): postcss.Rule {

        if (!selector) return null;

        const selectors: string[] = [];

        const type: CssRuleSelectorType = this.getCssRuleSelectorType(selector);

        const value: string = type === CssRuleSelectorType.Tag ? selector : selector.slice(1);

        const substitution: string = type === CssRuleSelectorType.Tag ? replacement : replacement.slice(1);

        const parser: Processor = selectorParser();

        // Process each selector in the rule
        for (const current of rule.selectors) {

            // Walk the selector AST for this selector
            const ast: AstRoot = parser.astSync(current);

            // Replace matching nodes with the replacement.
            switch (type) {
                case CssRuleSelectorType.Class:
                    ast.walkClasses(node => {
                        if (node.value === value) node.value = substitution;
                    });
                    break;

                case CssRuleSelectorType.Id:
                    ast.walkIds(node => {
                        if (node.value === value) node.value = substitution;
                    });
                    break;

                case CssRuleSelectorType.Tag:
                    ast.walkTags(node => {
                        if (node.value.toLowerCase() === value.toLowerCase()) node.value = substitution;
                    });
                    break;
            }

            const clean: string = processor.processSync(ast.toString()).trim();

            if (!!clean) selectors.push(clean);
        }

        if (!selectors.length) return null;

        // Create a new rule with the new selectors.
        const props: RuleProps = { selector: selectors.join(", ") };

        // Clone declarations from original rule.
        const children: postcss.NewChild[] = rule.nodes.map(node => node.clone());

        return postcss.rule(props).append(...children);
    }


    public getCssDimensionPropertyPattern(property: string): string {
        // Make sure the property is matched only if it is a word.
        const propertyWord: string = this.getCssWordPattern(property);

        // Match and capture the value (number) and unit (string) of the property.
        const valuePattern: string = "(?<value>[0-9]+)\\s*?(?<unit>[a-zA-Z]+)?";

        // Search for the string <property>: <value><unit>.
        return `${propertyWord}\\s*?:\\s*?${valuePattern}`;
    }

    public getCssRulePattern(selector: string): string {
        // Makes sure the selector is preceded by a closing curly brace
        // (i.e. the end of a CSS block) or the beginning of the string.
        const formatLookbehind: string = "(?<=(?:}|^)[^{}]*)";

        // Make sure the selector is matched only if it is a word.
        const selectorWord: string = this.getCssWordPattern(selector);

        // Matches a block of text inside curly braces.
        const blockPattern: string = "\\{[^{}]+}";

        // Returns: (?<=(?:}|^)[^{}]*)(?<![\w-])<selector>(?![\w-])\s*\{[^{}]+}
        return `${formatLookbehind}${selectorWord}\\s*${blockPattern}`;
    }

    public getCssWordPattern(word: string): string {
        // Redefinition of word boundaries because \b is not enough, as '-' is considered a word character here.
        const wordBoundaries: string[] = ["(?<![\\w-])", "(?![\\w-])"];

        // Escapes regex reserved characters present in the given word.
        const escapedWord: string = this.regexUtilityService.escapeRegex(word);

        // Returns: (?<![\w-])<word>(?![\w-])
        return `${wordBoundaries[0]}${escapedWord}${wordBoundaries[1]}`;
    }
}